import { getHasSettledOnce } from '@/lib/bootGateStore';
import { isPreReactEarlyReleasePath } from '@/lib/authPublicPaths';
import { isColdBootSessionActive } from '@/lib/preReactBootHandoff';
import {
  GLOBAL_OVERLAY_MIN_VISIBLE_MS,
  GLOBAL_OVERLAY_SHOW_DELAY_MS,
} from '@/lib/overlayTimingConstants';
import type {
  LoadingOwner,
  LoadingPhase,
  LoadingRequest,
  LoadingSource,
  LoadingSurfaceVariant,
} from './types';

export const SOURCE_PRIORITY: Record<LoadingSource, number> = {
  boot: 100,
  'auth-login': 80,
  'route-nav': 60,
  'admin-gate': 55,
  'page-data': 40,
  'table-data': 35,
  'dashboard-data': 35,
  'inline-button': 10,
  'background-refresh': 5,
};

export const SOURCE_PHASE: Record<LoadingSource, LoadingPhase> = {
  boot: 'boot',
  'auth-login': 'auth',
  'route-nav': 'route',
  'admin-gate': 'route',
  'page-data': 'data',
  'table-data': 'data',
  'dashboard-data': 'data',
  'inline-button': 'inline',
  'background-refresh': 'background',
};

/** Warm nav: route chunk visual suppressed — data owner merges into single content phase. */
export const ROUTE_CHUNK_MIN_VISIBLE_MS = 150;

export const POST_LOGIN_TRANSITION_MS = 1500;

export const DATA_SOURCES: LoadingSource[] = ['page-data', 'table-data', 'dashboard-data', 'admin-gate'];

const COLD_BOOT_SURFACE_SOURCES: LoadingSource[] = [
  'boot',
  'admin-gate',
  'route-nav',
  ...DATA_SOURCES,
];

/**
 * F5/hard reload: suppress coordinator content until admin gate/route/data settle.
 * Auth forms + public share (`isPreReactEarlyReleasePath`) are excluded — no AdminLayout settle.
 */
export function isColdBootTransitionActive(requests: LoadingRequest[]): boolean {
  if (typeof window !== 'undefined' && isPreReactEarlyReleasePath(window.location.pathname)) {
    return false;
  }
  if (!isColdBootSessionActive()) return false;
  if (!getHasSettledOnce()) return true;
  return requests.some((r) => COLD_BOOT_SURFACE_SOURCES.includes(r.source));
}

function pickDataOrRoute(sorted: LoadingRequest[]): LoadingRequest | null {
  const data = sorted.filter((r) => DATA_SOURCES.includes(r.source));
  if (data.length > 0) {
    return data.sort((a, b) => {
      if (b.priority !== a.priority) return b.priority - a.priority;
      return b.startedAt - a.startedAt;
    })[0] ?? null;
  }
  return sorted.find((r) => r.source === 'route-nav') ?? null;
}

const SURFACE_SOURCES = new Set<LoadingSource>([
  'boot',
  'auth-login',
  'route-nav',
  'admin-gate',
  'page-data',
  'table-data',
  'dashboard-data',
]);

export function isSurfaceSource(source: LoadingSource): boolean {
  return SURFACE_SOURCES.has(source);
}

export function sourceToOwner(source: LoadingSource): LoadingOwner {
  switch (source) {
    case 'boot':
      return 'boot';
    case 'auth-login':
      return 'login';
    case 'route-nav':
      return 'content';
    case 'table-data':
      return 'table';
    case 'page-data':
    case 'dashboard-data':
    case 'admin-gate':
      return 'content';
    default:
      return 'none';
  }
}

export function sourceToSurface(source: LoadingSource, isWarmNav: boolean): LoadingSurfaceVariant {
  if (source === 'boot') return 'fullscreen';
  if (source === 'auth-login') return 'none';
  if (source === 'route-nav') {
    /** Always compact content — no route banner, no route→content sequence. */
    return 'content';
  }
  if (DATA_SOURCES.includes(source)) return 'content';
  return 'none';
}

export function pickActiveRequest(
  requests: LoadingRequest[],
  options: {
    isWarmNav: boolean;
    postLoginUntil: number;
    suppressUntil: number;
    now?: number;
  },
): LoadingRequest | null {
  const now = options.now ?? Date.now();
  const visual = requests.filter((r) => isSurfaceSource(r.source));
  if (visual.length === 0) return null;

  const sorted = [...visual].sort((a, b) => {
    if (b.priority !== a.priority) return b.priority - a.priority;
    return b.startedAt - a.startedAt;
  });

  const boot = sorted.find((r) => r.source === 'boot');
  if (boot && !getHasSettledOnce()) return boot;

  if (options.postLoginUntil > now) {
    return pickDataOrRoute(sorted);
  }

  if (options.suppressUntil > now) {
    return pickDataOrRoute(sorted);
  }

  const dataOrRoute = pickDataOrRoute(sorted);
  if (dataOrRoute) return dataOrRoute;

  return sorted[0] ?? null;
}

export function resolveVisibleSurface(
  request: LoadingRequest | null,
  isWarmNav: boolean,
  options?: { coldBootTransitionActive?: boolean },
): LoadingSurfaceVariant {
  if (options?.coldBootTransitionActive) return 'none';
  if (!request) return 'none';
  return sourceToSurface(request.source, isWarmNav);
}

export function resolveOwner(request: LoadingRequest | null): LoadingOwner {
  if (!request) return 'none';
  return sourceToOwner(request.source);
}

export const BOOT_OVERLAY_HANDOFF_MS = GLOBAL_OVERLAY_SHOW_DELAY_MS + GLOBAL_OVERLAY_MIN_VISIBLE_MS;
